import React, { useState, useEffect } from 'react';
import { X, BarChart3, Bell, Phone, UserCircle, History, Package, FileText, Target, Megaphone, Linkedin, MousePointerClick, Link2, Check, ArrowLeft, RefreshCw, Download, ShoppingCart, Loader2, Truck, ExternalLink, User, Clock } from 'lucide-react';
import LeadTimeline from './LeadTimeline';
import { Lead } from '@/types/leads/Table';
import DynamicLeadForm from '../Forms/Leads/DynamicLeadForm';
import FloatingOrderPanel from './FloatingOrderPanel';
import ReminderForm from '../Forms/Leads/ReminderForm';
import { optimizedApi } from '@/utils/api/optimized-api';
import { getLeadEvents } from '@/actions/data-fetch-activities';
import { format } from 'date-fns';
import InitiateCallModal from '../OutboundCalling/InitiateCallModal';
import { getCachedOutboundConfig, cacheOutboundConfig, OutboundCallConfig } from '@/utils/outbound-calling';
import { useCallingState } from '@/contexts/CallingContext';
import { showToast } from '@/components/ui/Toast';
import { refreshOrderStatus } from '@/services/razorpayService';

// Safe date formatting helper - dates stored as IST in DB, display as-is
const getDeliveryStatusBadge = (statusCode: number | null | undefined): { label: string; className: string } => {
    switch (statusCode) {
        case 8:
        case 48: return { label: 'Delivered', className: 'bg-green-100 text-green-800' };
        case 6:  return { label: 'Out for Delivery', className: 'bg-blue-100 text-blue-800' };
        case 4:  return { label: 'Picked Up', className: 'bg-indigo-100 text-indigo-800' };
        case 9:  return { label: 'Failed Delivery', className: 'bg-red-100 text-red-800' };
        case 14: return { label: 'RTO Delivered', className: 'bg-orange-100 text-orange-800' };
        case 12:
        case 21: return { label: 'RTO', className: 'bg-orange-100 text-orange-800' };
        case 11: return { label: 'RTO Requested', className: 'bg-orange-50 text-orange-700' };
        case 5:  return { label: 'In Transit', className: 'bg-yellow-100 text-yellow-800' };
        case 1:
        case 28: return { label: 'Registered', className: 'bg-gray-100 text-gray-700' };
        default: return statusCode != null
            ? { label: 'In Transit', className: 'bg-yellow-100 text-yellow-800' }
            : { label: 'No tracking', className: 'bg-gray-100 text-gray-500' };
    }
};

const safeFormatDate = (dateValue: any, formatString: string, fallback: string = 'Invalid date') => {
    try {
        if (!dateValue) return fallback;
        const date = new Date(dateValue);
        if (isNaN(date.getTime())) return fallback;
        return format(date, formatString);
    } catch (error) {
        return fallback;
    }
};

interface UserOption {
    value: string;
    label: string;
    email?: string;
    role?: string;
}

interface LeadDetailsSidebarProps {
    isOpen: boolean;
    onClose: () => void;
    lead: Lead | null;
    onUpdate?: () => void;
    activeUsersForDropdown?: UserOption[];
    mode?: 'modal' | 'page';
}



interface LeadReminder {
    uuid: string;
    event_title: string;
    db_start: string;
    alert_before: string;
    created_at?: string;
    event_created_at?: string;
    lead_uuid: string;
}

interface CallHistory {
    id: number;
    uuid: string;
    provider: string;
    call_type: 'inbound' | 'outbound';
    call_status: string;
    caller_number: string;
    destination_number: string;
    agent_number?: string;
    agent_name?: string;
    total_duration?: string | number;
    recording_url?: string;
    hangup_cause?: string;
    is_missed: boolean;
    start_time?: string;
    created_at: string;
    raw_payload?: {
        agent_name?: string;
        agent_number?: string;
        hangup_cause?: string;
    };
}

interface ShopifyCustomer {
    customer_id: number;
    uuid: string;
    shopify_customer_id: string;
    client_id_fk: number;
    email?: string;
    phone?: string;
    first_name?: string;
    last_name?: string;
    orders_count?: number;
    total_spent?: string;
    currency?: string;
    last_order_id?: string;
    last_order_name?: string;
    state?: string;
    verified_email?: boolean;
    tax_exempt?: boolean;
    tags?: string;
    note?: string;
    email_marketing_consent?: {
        state?: string;
        opt_in_level?: string;
        consent_updated_at?: string;
    };
    sms_marketing_consent?: {
        state?: string;
        opt_in_level?: string;
        consent_updated_at?: string;
        consent_collected_from?: string;
    };
    default_address?: {
        address1?: string;
        address2?: string;
        city?: string;
        province?: string;
        country?: string;
        zip?: string;
        phone?: string;
    };
    addresses?: any[];
    shopify_created_at?: string;
    shopify_updated_at?: string;
    created_at?: string;
    updated_at?: string;
    last_synced_at?: string;
}

interface CheckoutLineItem {
    title: string;
    variant_title?: string;
    quantity: number;
    price: number;
    total: number;
    sku?: string;
}

interface CheckoutRecord {
    lead_id: number;
    uuid: string;
    phone?: string;
    email?: string;
    name?: string;
    checkout_id?: string;
    checkout_token?: string;
    checkout_url?: string;
    checkout_date?: string;
    cart_value?: number;
    currency?: string;
    cart_token?: string;
    customer_type?: string;
    status?: number;
    status_uuid?: string;
    created_date?: string;
    order_id?: string;
    statusDisplay?: string;
    isConverted?: boolean;
    line_items?: CheckoutLineItem[];
    items_count?: number;
    source?: string;
    drop_stage?: string;
}

interface TrackingInfo {
    tracking_numbers: string[];
    tracking_urls: string[];
    tracking_company: string | null;
}

interface ShopifyOrder {
    id: number;
    uuid?: string;
    shopify_order_id: string;
    shopify_customer_id?: string;
    order_number?: string;
    order_name?: string;
    email?: string;
    customer_phone?: string;
    total_price?: string;
    subtotal_price?: string;
    total_tax?: string;
    total_discounts?: string;
    currency?: string;
    financial_status?: string;
    fulfillment_status?: string;
    order_status?: string;
    order_type?: string; // 'draft' | 'order'
    source_name?: string; // 'razorpay' | 'cod' | 'checkout_link'
    order_date?: string; // Actual Shopify order date
    processed_at?: string;
    created_at?: string;
    // Payment link info (for partial payments)
    latest_payment_link?: {
        short_url?: string;
        status?: string;
        amount?: number;  // In paise
        amount_paid?: number;  // In paise
        razorpay_payment_id?: string;
        payment_method?: string;
        whatsapp_sent?: boolean;
        paid_at?: string;
    };
    updated_at?: string;
    cancelled_at?: string;
    cancel_reason?: string;
    tags?: string;
    tracking_info?: TrackingInfo | null;
    line_items_count?: number;
    total_items?: number;
    lineItems?: any[];
    latest_delivery_status?: number | null;
    latest_delivery_status_desc?: string | null;
    latest_delivery_location?: string | null;
    latest_delivery_event_at?: string | null;
}

interface ReorderItem {
    title: string;
    variant_title?: string;
    quantity: number;
    reorder_due_date: string;
    reorder_days: number;
    delivered_at: string;
    days_overdue: number;
    is_overdue: boolean;
    order: {
        order_number: string;
        delivered_at: string;
    };
}

interface ReorderInfo {
    lead_summary: {
        name: string | null;
        phone: string | null;
        current_status: string | null;
        total_orders_count: number;
    };
    reorder_items: ReorderItem[];
    total_reorder_items: number;
}

const LeadDetailsSidebar: React.FC<LeadDetailsSidebarProps> = ({
    isOpen,
    onClose,
    lead,
    onUpdate,
    activeUsersForDropdown = [],
    mode = 'modal'
}) => {
    const isPageMode = mode === 'page';
    const [activeTab, setActiveTab] = useState<string>('customerinfo');
    const [reminders, setReminders] = useState<LeadReminder[]>([]);
    const [callHistory, setCallHistory] = useState<CallHistory[]>([]);
    const [shopifyCustomer, setShopifyCustomer] = useState<ShopifyCustomer | null>(null);
    const [checkoutHistory, setCheckoutHistory] = useState<CheckoutRecord[]>([]);
    const [orderHistory, setOrderHistory] = useState<ShopifyOrder[]>([]);
    const [reorderInfo, setReorderInfo] = useState<ReorderInfo | null>(null);

    // Customer profiling state
    const [customerProfile, setCustomerProfile] = useState<{
        gender: string;
        dob: string;
        age: string;
        health_issues: string;
        height_cm: string;
        weight_kg: string;
        primary_goal: string;
        family_medical_history: string;
    }>({
        gender: '',
        dob: '',
        age: '',
        health_issues: '',
        height_cm: '',
        weight_kg: '',
        primary_goal: '',
        family_medical_history: '',
    });
    const [isSavingProfile, setIsSavingProfile] = useState(false);

    // Outbound calling state
    const [outboundConfig, setOutboundConfig] = useState<OutboundCallConfig | null>(null);
    const [isCallModalOpen, setIsCallModalOpen] = useState(false);
    const { isCallInProgress, setCallInProgress } = useCallingState();

    // Share link state
    const [isLinkCopied, setIsLinkCopied] = useState(false);

    // Shopify sync state
    const [isSyncingCustomer, setIsSyncingCustomer] = useState(false);
    const [isSyncingOrders, setIsSyncingOrders] = useState(false);

    // Order refresh state - tracks which order is currently being refreshed
    const [refreshingOrderId, setRefreshingOrderId] = useState<string | null>(null);

    // Floating order panel state
    const [isOrderPanelOpen, setIsOrderPanelOpen] = useState(false);

    // Copy shareable link to clipboard
    const handleCopyShareLink = async () => {
        if (!lead?.uuid) return;

        const shareUrl = `${window.location.origin}/lead/${lead.uuid}`;
        try {
            await navigator.clipboard.writeText(shareUrl);
            setIsLinkCopied(true);
            showToast('Link copied to clipboard', 'success');
            // Reset icon after 2 seconds
            setTimeout(() => setIsLinkCopied(false), 2000);
        } catch (error) {
            console.error('Failed to copy link:', error);
            showToast('Failed to copy link', 'error');
        }
    };

    // Sync Shopify customer from Shopify API
    const handleSyncShopifyCustomer = async () => {
        // Use lead's customer ID or fallback to local shopifyCustomer state (for cases where we just linked)
        const customerId = lead?.shopify_customer_id || shopifyCustomer?.shopify_customer_id;
        if (!customerId) {
            showToast('No Shopify customer ID available', 'error');
            return;
        }

        setIsSyncingCustomer(true);
        try {
            const response = await optimizedApi.post<ShopifyCustomer>(`/leads/shopify-customer/${customerId}/sync`, {});
            if (response.success && response.data) {
                setShopifyCustomer(response.data as ShopifyCustomer);
                showToast(response.message || 'Customer data synced from Shopify', 'success');
            } else {
                showToast(response.message || 'Failed to sync customer', 'error');
            }
        } catch (error: any) {
            console.error('Error syncing Shopify customer:', error);
            showToast(error.message || 'Failed to sync customer from Shopify', 'error');
        } finally {
            setIsSyncingCustomer(false);
        }
    };

    // Find and link Shopify customer by phone number
    const handleFindAndLinkCustomer = async () => {
        if (!lead?.uuid) {
            showToast('Lead not available', 'error');
            return;
        }

        setIsSyncingCustomer(true);
        try {
            // Response includes data plus extra fields at root level (linked, needsSync, source)
            const response = await optimizedApi.post<ShopifyCustomer>(`/leads/shopify-customer/${lead.uuid}/find-and-link`, {}) as any;
            if (response.success && response.data) {
                // If customer found from orders but not in shopify_customers, we need to sync from Shopify
                if (response.needsSync && response.data.shopify_customer_id) {
                    showToast('Customer ID found! Syncing full data from Shopify...', 'info');

                    // Automatically sync customer data from Shopify
                    try {
                        const syncResponse = await optimizedApi.post<ShopifyCustomer>(`/leads/shopify-customer/${response.data.shopify_customer_id}/sync`, {});
                        if (syncResponse.success && syncResponse.data) {
                            setShopifyCustomer(syncResponse.data as ShopifyCustomer);
                            showToast('Customer found, linked, and synced successfully!', 'success');
                        } else {
                            // Sync failed but customer is still linked - show partial success
                            showToast('Customer linked but sync failed. Try syncing manually.', 'warning');
                            // Still set the minimal data so user knows a customer is linked
                            setShopifyCustomer({ shopify_customer_id: response.data.shopify_customer_id } as ShopifyCustomer);
                        }
                    } catch (syncError: any) {
                        console.error('Error syncing customer data:', syncError);
                        showToast('Customer linked but sync failed. Try syncing manually.', 'warning');
                        setShopifyCustomer({ shopify_customer_id: response.data.shopify_customer_id } as ShopifyCustomer);
                    }
                } else {
                    // Full customer data returned
                    setShopifyCustomer(response.data as ShopifyCustomer);
                    if (response.linked) {
                        showToast('Customer found and linked successfully!', 'success');
                    } else {
                        showToast(response.message || 'Customer already linked', 'success');
                    }
                }
            } else {
                showToast(response.message || 'No matching customer found', 'error');
            }
        } catch (error: any) {
            console.error('Error finding/linking Shopify customer:', error);
            showToast(error.message || 'No Shopify customer found with this phone number', 'error');
        } finally {
            setIsSyncingCustomer(false);
        }
    };

    // Sync order history from Shopify API
    const handleSyncOrders = async () => {
        // Use lead's customer ID or fallback to local shopifyCustomer state
        const customerId = lead?.shopify_customer_id || shopifyCustomer?.shopify_customer_id;
        if (!customerId) {
            showToast('No Shopify customer ID available', 'error');
            return;
        }

        setIsSyncingOrders(true);
        try {
            const response = await optimizedApi.post(`/leads/order-history/${customerId}/sync`, {});
            if (response.success) {
                showToast(response.message || 'Orders synced successfully', 'success');
                // Refetch order history to get updated list
                if (lead?.uuid) {
                    const orderRes = await optimizedApi.get<ShopifyOrder[]>(`/leads/order-history/${lead.uuid}`);
                    if (orderRes.success && orderRes.data) {
                        setOrderHistory(Array.isArray(orderRes.data) ? orderRes.data : []);
                    }
                }
            } else {
                showToast(response.message || 'Failed to sync orders', 'error');
            }
        } catch (error: any) {
            console.error('Error syncing orders:', error);
            showToast(error.message || 'Failed to sync orders from Shopify', 'error');
        } finally {
            setIsSyncingOrders(false);
        }
    };

    // Refresh individual order status from Shopify
    const handleRefreshOrder = async (orderUuid: string) => {
        if (!orderUuid) {
            showToast('Order UUID not available', 'error');
            return;
        }

        setRefreshingOrderId(orderUuid);
        try {
            const response = await refreshOrderStatus(orderUuid);
            if (response.success && response.data) {
                const orderData = response.data.order;
                const paymentLinkData = response.data.payment_link;

                // Update the order in the local state with all returned fields
                setOrderHistory(prevOrders =>
                    prevOrders.map(order =>
                        order.uuid === orderUuid
                            ? {
                                ...order,
                                shopify_order_id: orderData.shopify_order_id || order.shopify_order_id,
                                order_number: orderData.order_number || order.order_number,
                                order_name: orderData.order_name || order.order_name,
                                total_price: String(orderData.total_price) || order.total_price,
                                subtotal_price: String(orderData.subtotal_price) || order.subtotal_price,
                                total_discounts: String(orderData.total_discounts) || order.total_discounts,
                                financial_status: orderData.financial_status,
                                fulfillment_status: orderData.fulfillment_status || undefined,
                                order_status: orderData.order_status,
                                order_type: orderData.order_type,
                                source_name: orderData.source_name,
                                tags: orderData.tags,
                                tracking_info: orderData.tracking_info,
                                lineItems: orderData.line_items || order.lineItems,
                                line_items_count: orderData.line_items_count,
                                updated_at: orderData.updated_at,
                                // Update payment link info if available
                                latest_payment_link: paymentLinkData ? {
                                    short_url: paymentLinkData.short_url || order.latest_payment_link?.short_url,
                                    status: paymentLinkData.status,
                                    amount: paymentLinkData.amount || order.latest_payment_link?.amount || 0,
                                    amount_paid: paymentLinkData.amount_paid || 0,
                                    razorpay_payment_id: paymentLinkData.razorpay_payment_id || order.latest_payment_link?.razorpay_payment_id,
                                    payment_method: paymentLinkData.payment_method || order.latest_payment_link?.payment_method,
                                    paid_at: paymentLinkData.paid_at || order.latest_payment_link?.paid_at,
                                } : order.latest_payment_link,
                              }
                            : order
                    )
                );
                showToast('Order status updated', 'success');
            } else {
                showToast(response.error || 'Failed to refresh order', 'error');
            }
        } catch (error: any) {
            console.error('Error refreshing order:', error);
            showToast(error.message || 'Failed to refresh order status', 'error');
        } finally {
            setRefreshingOrderId(null);
        }
    };

    // Get client industry from localStorage
    const getClientIndustry = (): string => {
        try {
            const userData = JSON.parse(localStorage.getItem('crm_user') || '{}');
            const data = userData.data || userData;
            return data.client?.industry || '';
        } catch (error) {
            console.error('Error getting client industry:', error);
            return '';
        }
    };

    const isEcommerce = getClientIndustry().toLowerCase() === 'ecommerce';


    // Track which tabs have been loaded to avoid unnecessary API calls
    const [loadedTabs, setLoadedTabs] = useState<Set<string>>(new Set(['form'])); // Form is loaded by default
    const [loadingTabs, setLoadingTabs] = useState<Set<string>>(new Set());

    // Reset state when modal closes or new lead is selected
    useEffect(() => {
        if (lead && isOpen) {
            // Reset state for new lead
            setLoadedTabs(new Set());
            setLoadingTabs(new Set());
            setReminders([]);
            setCallHistory([]);
            setShopifyCustomer(null);
            setCheckoutHistory([]);
            setOrderHistory([]);
            setCustomerProfile({ gender: '', dob: '', age: '', health_issues: '', height_cm: '', weight_kg: '', primary_goal: '', family_medical_history: '' });
            // Set default tab based on industry
            const defaultTab = isEcommerce ? 'customerinfo' : 'timeline';
            setActiveTab(defaultTab);

            // Auto-load data for default tab (force=true to bypass stale loadedTabs check)
            if (isEcommerce) {
                fetchShopifyCustomerOnly(true);
            }
        }
    }, [lead?.uuid, isOpen, isEcommerce]);

    // Load outbound config on mount (from cache or API)
    useEffect(() => {
        const loadOutboundConfig = async () => {
            // First try to get from cache
            const cachedConfig = getCachedOutboundConfig();
            if (cachedConfig) {
                setOutboundConfig(cachedConfig);
                return;
            }

            // If not cached, fetch from API
            try {
                const response = await optimizedApi.get('/outbound-call/config');

                if (response.success && response.data) {
                    const data = response.data as any;
                    if (data.uuid) {
                        const config: OutboundCallConfig = {
                            uuid: data.uuid,
                            provider: data.provider,
                            provider_name: data.provider_name,
                            is_active: data.is_active
                        };
                        // Cache the config
                        cacheOutboundConfig(config);
                        setOutboundConfig(config);
                    } else {
                        setOutboundConfig(null);
                    }
                } else {
                    // No config exists
                    setOutboundConfig(null);
                }
            } catch (error) {
                console.error('❌ Error loading outbound config:', error);
                setOutboundConfig(null);
            }
        };
        loadOutboundConfig();
    }, []);

    // Handle tab switching with lazy loading
    const handleTabChange = (tabId: string) => {
        setActiveTab(tabId as any);

        // Load data for the tab if not already loaded
        switch (tabId) {
            case 'customerinfo':
                fetchShopifyCustomerOnly();
                break;
            case 'checkouthistory':
                fetchCheckoutHistoryOnly();
                break;
            case 'orderhistory':
                fetchOrderHistoryOnly();
                break;
            case 'reorders':
                fetchReorderInfoOnly();
                break;
            case 'timeline':
                // LeadTimeline is self-contained, no external fetch needed
                break;
            case 'marketing':
                fetchRemindersOnly();
                break;
            case 'reminders':
                fetchRemindersOnly();
                break;
            case 'calls':
                fetchCallHistoryOnly();
                break;
            // 'form' doesn't need additional data loading
        }
    };

    // Fetch customer profiling data
    const fetchCustomerProfile = async () => {
        if (!lead?.uuid) return;
        try {
            const res = await optimizedApi.get<any>(`/leads/${lead.uuid}/customer-profile`);
            if (res.success && res.data) {
                const d = res.data;
                setCustomerProfile({
                    gender: d.gender || '',
                    dob: d.dob || '',
                    age: d.age != null ? String(d.age) : '',
                    health_issues: d.health_issues || '',
                    height_cm: d.height_cm || '',
                    weight_kg: d.weight_kg || '',
                    primary_goal: d.primary_goal || '',
                    family_medical_history: d.family_medical_history || '',
                });
            }
        } catch (_) { /* silently ignore */ }
    };

    // Save customer profiling data
    const handleSaveProfile = async () => {
        if (!lead?.uuid) return;
        setIsSavingProfile(true);
        try {
            const payload: any = {
                gender: customerProfile.gender || null,
                dob: customerProfile.dob || null,
                age: customerProfile.age ? parseInt(customerProfile.age, 10) : null,
                health_issues: customerProfile.health_issues || null,
                height_cm: customerProfile.height_cm || null,
                weight_kg: customerProfile.weight_kg || null,
                primary_goal: customerProfile.primary_goal || null,
                family_medical_history: customerProfile.family_medical_history || null,
            };
            const res = await optimizedApi.put<any>(`/leads/${lead.uuid}/customer-profile`, payload);
            if (res.success) {
                // Update age if it was auto-calculated from DOB
                if (res.data?.age != null) {
                    setCustomerProfile(prev => ({ ...prev, age: String(res.data.age) }));
                }
                showToast('Customer profile saved', 'success');
            } else {
                showToast('Failed to save profile', 'error');
            }
        } catch (err: any) {
            showToast(err.message || 'Failed to save profile', 'error');
        } finally {
            setIsSavingProfile(false);
        }
    };

    // Fetch Shopify customer details
    const fetchShopifyCustomerOnly = async (force: boolean = false) => {
        if (!lead || (!force && loadedTabs.has('customerinfo'))) return;

        setLoadingTabs(prev => new Set([...prev, 'customerinfo']));

        // Fetch customer profile in parallel (non-blocking)
        fetchCustomerProfile();

        try {
            const customerRes = await optimizedApi.get<ShopifyCustomer>(`/leads/shopify-customer/${lead.uuid}`);
            if (customerRes.success && customerRes.data) {
                setShopifyCustomer(customerRes.data);
            } else {
                // Auto-find and link customer if not found (searches Shopify API)
                await autoFindAndLinkCustomer();
            }
        } catch (error) {
            // Auto-find and link customer on error (likely 404)
            await autoFindAndLinkCustomer();
        } finally {
            setLoadedTabs(prev => new Set([...prev, 'customerinfo']));
            setLoadingTabs(prev => {
                const newSet = new Set(prev);
                newSet.delete('customerinfo');
                return newSet;
            });
        }
    };

    // Auto-find and link Shopify customer silently (no toast on failure)
    const autoFindAndLinkCustomer = async () => {
        if (!lead?.uuid || !lead?.phone) {
            setShopifyCustomer(null);
            return;
        }

        try {
            const response = await optimizedApi.post<ShopifyCustomer>(`/leads/shopify-customer/${lead.uuid}/find-and-link`, {}) as any;
            if (response.success && response.data) {
                // If customer found from orders but not in shopify_customers, sync from Shopify
                if (response.needsSync && response.data.shopify_customer_id) {
                    try {
                        const syncResponse = await optimizedApi.post<ShopifyCustomer>(`/leads/shopify-customer/${response.data.shopify_customer_id}/sync`, {});
                        if (syncResponse.success && syncResponse.data) {
                            setShopifyCustomer(syncResponse.data as ShopifyCustomer);
                        } else {
                            setShopifyCustomer({ shopify_customer_id: response.data.shopify_customer_id } as ShopifyCustomer);
                        }
                    } catch (syncError) {
                        setShopifyCustomer({ shopify_customer_id: response.data.shopify_customer_id } as ShopifyCustomer);
                    }
                } else {
                    setShopifyCustomer(response.data as ShopifyCustomer);
                }
            } else {
                setShopifyCustomer(null);
            }
        } catch (error) {
            setShopifyCustomer(null);
        }
    };

    // Fetch checkout history
    const fetchCheckoutHistoryOnly = async () => {
        if (!lead || loadedTabs.has('checkouthistory')) return;

        setLoadingTabs(prev => new Set([...prev, 'checkouthistory']));

        try {
            const checkoutRes = await optimizedApi.get<{ checkouts: CheckoutRecord[], primary_lead: any } | CheckoutRecord[]>(`/leads/checkout-history/${lead.uuid}`);
            if (checkoutRes.success && checkoutRes.data) {
                // Handle both response formats: { checkouts: [...] } or direct array
                const checkouts = Array.isArray(checkoutRes.data)
                    ? checkoutRes.data
                    : (checkoutRes.data.checkouts || []);
                setCheckoutHistory(checkouts);
            } else {
                setCheckoutHistory([]);
            }
        } catch (error) {
            setCheckoutHistory([]);
        } finally {
            setLoadedTabs(prev => new Set([...prev, 'checkouthistory']));
            setLoadingTabs(prev => {
                const newSet = new Set(prev);
                newSet.delete('checkouthistory');
                return newSet;
            });
        }
    };

    // Fetch order history
    const fetchOrderHistoryOnly = async () => {
        if (!lead || loadedTabs.has('orderhistory')) return;

        setLoadingTabs(prev => new Set([...prev, 'orderhistory']));

        try {
            const orderRes = await optimizedApi.get<ShopifyOrder[]>(`/leads/order-history/${lead.uuid}`);
            if (orderRes.success && orderRes.data) {
                setOrderHistory(orderRes.data);
            } else {
                setOrderHistory([]);
            }
        } catch (error) {
            setOrderHistory([]);
        } finally {
            setLoadedTabs(prev => new Set([...prev, 'orderhistory']));
            setLoadingTabs(prev => {
                const newSet = new Set(prev);
                newSet.delete('orderhistory');
                return newSet;
            });
        }
    };

    // Fetch reorder info
    const fetchReorderInfoOnly = async () => {
        if (!lead || loadedTabs.has('reorders')) return;

        setLoadingTabs(prev => new Set([...prev, 'reorders']));

        try {
            const reorderRes = await optimizedApi.get<ReorderInfo>(`/leads/reorder-info/${lead.uuid}`);
            if (reorderRes.success && reorderRes.data) {
                setReorderInfo(reorderRes.data);
            } else {
                setReorderInfo(null);
            }
        } catch (error) {
            setReorderInfo(null);
        } finally {
            setLoadedTabs(prev => new Set([...prev, 'reorders']));
            setLoadingTabs(prev => {
                const newSet = new Set(prev);
                newSet.delete('reorders');
                return newSet;
            });
        }
    };


    // Fetch reminders/events for marketing and reminders tabs
    const fetchRemindersOnly = async () => {
        if (!lead || (loadedTabs.has('marketing') && loadedTabs.has('reminders'))) return;

        // Determine which tab we're loading for
        const currentTab = activeTab === 'reminders' ? 'reminders' : 'marketing';
        setLoadingTabs(prev => new Set([...prev, currentTab]));
        try {
            const remindersRes = await getLeadEvents(lead.uuid);
            // Handle both ApiResponse and fallback { data: [] } response
            if ('success' in remindersRes && remindersRes.success) {
                setReminders((remindersRes.data as LeadReminder[]) || []);
            } else if ('data' in remindersRes) {
                // Fallback case when endpoint returns { data: [] }
                setReminders((remindersRes.data as LeadReminder[]) || []);
            } else {
                setReminders([]);
            }
            // Mark both marketing and reminders as loaded since they share the same data
            setLoadedTabs(prev => new Set([...prev, 'marketing', 'reminders']));
        } catch (error) {
            setReminders([]);
        } finally {
            setLoadingTabs(prev => {
                const newSet = new Set(prev);
                newSet.delete(currentTab);
                return newSet;
            });
        }
    };

    // Fetch call history for calls tab
    const fetchCallHistoryOnly = async () => {
        if (!lead || loadedTabs.has('calls')) return;

        setLoadingTabs(prev => new Set([...prev, 'calls']));
        try {
            const callsRes = await optimizedApi.get<CallHistory[]>(`/outbound-call/call-history/lead/${lead.uuid}`);
            if (callsRes.success) {
                // Limit to last 10 calls
                const callsArray = (callsRes.data as any)?.calls || callsRes.data || [];
                const recentCalls = (Array.isArray(callsArray) ? callsArray : []).slice(0, 10);
                setCallHistory(recentCalls);
            } else {
                setCallHistory([]);
            }
            setLoadedTabs(prev => new Set([...prev, 'calls']));
        } catch (error) {
            setCallHistory([]);
        } finally {
            setLoadingTabs(prev => {
                const newSet = new Set(prev);
                newSet.delete('calls');
                return newSet;
            });
        }
    };

    // Outbound calling handlers
    const handleInitiateCall = (event: React.MouseEvent) => {
        event.stopPropagation();

        if (isCallInProgress) {
            showToast('Another call is already in progress', 'error');
            return;
        }

        if (!lead?.phone) {
            showToast('No phone number available for this lead', 'error');
            return;
        }

        setIsCallModalOpen(true);
        setCallInProgress(true);
    };

    const handleCloseCallModal = () => {
        setIsCallModalOpen(false);
        setCallInProgress(false);
    };

    // Tabs for full-screen modal with left sidebar navigation
    // Order: Customer Info, Checkout History, Order History, Re-Orders, Lead Form, Remarks, Activity, Reminders, Calls, Marketing
    const tabs = [
        ...(isEcommerce ? [
            { id: 'customerinfo', label: 'Customer Info', icon: UserCircle },
            { id: 'checkouthistory', label: 'Checkout History', icon: History },
            { id: 'orderhistory', label: 'Order History', icon: Package },
            { id: 'reorders', label: 'Re-Orders', icon: RefreshCw },
        ] : []),
        { id: 'form', label: 'Lead Form', icon: FileText },
        { id: 'timeline', label: 'Timeline', icon: Clock },
        { id: 'reminders', label: 'Reminders', icon: Bell },
        { id: 'calls', label: 'Calls', icon: Phone },
        { id: 'marketing', label: 'Marketing', icon: BarChart3 },
    ];

    // In modal mode, don't render if closed or no lead
    // In page mode, the parent handles loading/error states
    if (!lead) return null;
    if (!isPageMode && !isOpen) return null;

    return (
        <>
            {/* Full-screen Modal Backdrop - Only in modal mode */}
            {!isPageMode && (
                <div
                    className={`fixed inset-0 bg-black z-[60] transition-opacity duration-300 ${
                        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
                    }`}
                    onClick={onClose}
                />
            )}

            {/* Full-screen Container */}
            <div className={`${isPageMode ? 'h-screen' : 'fixed inset-0 z-[70]'} bg-white flex flex-col ${
                !isPageMode ? `transform transition-all duration-300 ${isOpen ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}` : ''
            }`}>
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900 uppercase">{lead.name || lead.phone || 'Lead Details'}</h2>
                        <div className="flex items-center gap-2 mt-1">
                            <p className="text-sm font-medium text-gray-800">{lead.phone}</p>
                            {lead.email && <span className="text-sm font-medium text-gray-800">• {lead.email}</span>}
                            {/* Phone icon for calling */}
                            {outboundConfig && lead.phone && lead.phone.trim() !== '' && !isCallInProgress && (
                                <button
                                    onClick={handleInitiateCall}
                                    className="p-1.5 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-full transition-colors"
                                    title="Click to call"
                                    disabled={isCallInProgress}
                                >
                                    <Phone className="h-4 w-4" />
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {/* Share Link Button */}
                        <button
                            onClick={handleCopyShareLink}
                            className="p-2 hover:bg-gray-200 rounded-full transition-colors"
                            title="Copy shareable link"
                        >
                            {isLinkCopied ? (
                                <Check className="h-5 w-5 text-green-600" />
                            ) : (
                                <Link2 className="h-5 w-5 text-gray-600" />
                            )}
                        </button>
                        {/* Close/Back Button */}
                        {isPageMode ? (
                            <button
                                onClick={onClose}
                                className="flex items-center gap-2 px-3 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                            >
                                <ArrowLeft className="h-4 w-4" />
                                <span className="text-sm font-medium">Back to Leads</span>
                            </button>
                        ) : (
                            <button
                                onClick={onClose}
                                className="p-2 hover:bg-gray-200 rounded-full transition-colors"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        )}
                    </div>
                </div>

                {/* Main Content with Left Sidebar */}
                <div className="flex flex-1 overflow-hidden">
                    {/* Left Sidebar Navigation */}
                    <div className="w-48 bg-gray-50 border-r flex-shrink-0 overflow-y-auto">
                        <nav className="p-2 space-y-1">
                            {tabs.map((tab) => {
                                const Icon = tab.icon;
                                return (
                                    <button
                                        key={tab.id}
                                        onClick={() => handleTabChange(tab.id)}
                                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                                            activeTab === tab.id
                                                ? 'bg-brandColor text-white shadow-sm'
                                                : 'text-gray-700 hover:bg-gray-100'
                                        }`}
                                    >
                                        <Icon className="h-4 w-4 flex-shrink-0" />
                                        <span className="text-sm font-medium truncate">{tab.label}</span>
                                    </button>
                                );
                            })}
                        </nav>
                    </div>

                    {/* Tab Content */}
                    <div className="flex-1 overflow-y-auto bg-white">
                    {/* Customer Info Tab - two-column layout */}
                    {activeTab === 'customerinfo' && isEcommerce && (
                        <div className="flex h-full divide-x divide-gray-200">

                            {/* ── LEFT: Customer Information ── */}
                            <div className="w-1/2 flex flex-col overflow-y-auto">
                                <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-6 py-4 border-b border-green-100 flex items-center justify-between sticky top-0 z-10">
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-800">Customer Information</h3>
                                        <p className="text-xs text-gray-500 mt-0.5">Synced from Shopify</p>
                                    </div>
                                    {shopifyCustomer && (lead?.shopify_customer_id || shopifyCustomer?.shopify_customer_id) && (
                                        <button
                                            onClick={handleSyncShopifyCustomer}
                                            disabled={isSyncingCustomer}
                                            className="flex items-center gap-2 px-3 py-1.5 text-sm text-green-600 hover:text-green-700 hover:bg-green-100 rounded-lg transition-colors disabled:opacity-50"
                                            title="Refresh customer data from Shopify"
                                        >
                                            <RefreshCw className={`h-4 w-4 ${isSyncingCustomer ? 'animate-spin' : ''}`} />
                                            <span>{isSyncingCustomer ? 'Syncing...' : 'Sync'}</span>
                                        </button>
                                    )}
                                </div>
                                <div className="p-6 flex-1">
                                {loadingTabs.has('customerinfo') || isSyncingCustomer ? (
                                    <div className="flex items-center justify-center py-8">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brandColor"></div>
                                        <span className="ml-2 text-gray-600">{isSyncingCustomer ? 'Syncing from Shopify...' : 'Loading customer details...'}</span>
                                    </div>
                                ) : shopifyCustomer ? (
                                    <div className="space-y-6">
                                        {shopifyCustomer.shopify_customer_id && !shopifyCustomer.first_name && !shopifyCustomer.email && !shopifyCustomer.orders_count && (
                                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                                                <p className="text-sm text-yellow-800">
                                                    Customer ID linked but full details not synced. Click the <strong>Sync</strong> button above to fetch customer data from Shopify.
                                                </p>
                                            </div>
                                        )}
                                        <div className="bg-gray-50 rounded-lg p-4">
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <span className="text-sm text-gray-500">Customer Name</span>
                                                    <p className="text-sm font-medium text-gray-900">
                                                        {shopifyCustomer.first_name || shopifyCustomer.last_name
                                                            ? `${shopifyCustomer.first_name || ''} ${shopifyCustomer.last_name || ''}`.trim()
                                                            : 'N/A'}
                                                    </p>
                                                </div>
                                                <div>
                                                    <span className="text-sm text-gray-500">Email</span>
                                                    <p className="text-sm font-medium text-gray-900">{shopifyCustomer.email || 'N/A'}</p>
                                                </div>
                                                <div>
                                                    <span className="text-sm text-gray-500">Phone</span>
                                                    <p className="text-sm font-medium text-gray-900">
                                                        {shopifyCustomer.phone || shopifyCustomer.default_address?.phone || 'N/A'}
                                                    </p>
                                                </div>
                                                <div>
                                                    <span className="text-sm text-gray-500">Total Orders</span>
                                                    <p className="text-sm font-medium text-gray-900">{shopifyCustomer.orders_count || 0}</p>
                                                </div>
                                                <div>
                                                    <span className="text-sm text-gray-500">Total Spent</span>
                                                    <p className="text-sm font-medium text-green-600">
                                                        {shopifyCustomer.currency === 'INR' ? '₹' : shopifyCustomer.currency || '₹'} {shopifyCustomer.total_spent || '0.00'}
                                                    </p>
                                                </div>
                                                {shopifyCustomer.last_order_name && (
                                                    <div>
                                                        <span className="text-sm text-gray-500">Last Order</span>
                                                        <p className="text-sm font-medium text-gray-900">{shopifyCustomer.last_order_name}</p>
                                                    </div>
                                                )}
                                                <div>
                                                    <span className="text-sm text-gray-500">Email Verified</span>
                                                    <p className="text-sm font-medium">
                                                        {shopifyCustomer.verified_email ? (
                                                            <span className="text-green-600">Yes</span>
                                                        ) : (
                                                            <span className="text-gray-500">No</span>
                                                        )}
                                                    </p>
                                                </div>
                                                <div>
                                                    <span className="text-sm text-gray-500">Shopify Customer ID</span>
                                                    <p className="text-sm font-medium text-gray-900 font-mono">
                                                        {shopifyCustomer.shopify_customer_id || 'N/A'}
                                                    </p>
                                                </div>
                                                {shopifyCustomer.last_synced_at && (
                                                    <div>
                                                        <span className="text-sm text-gray-500">Last Synced</span>
                                                        <p className="text-sm font-medium text-gray-600">
                                                            {new Date(shopifyCustomer.last_synced_at).toLocaleString()}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        {shopifyCustomer.default_address && (
                                            <div>
                                                <h4 className="text-sm font-semibold text-gray-700 mb-2">Default Address</h4>
                                                <div className="bg-gray-50 rounded-lg p-4">
                                                    <p className="text-sm text-gray-900">
                                                        {shopifyCustomer.default_address.address1 && <span>{shopifyCustomer.default_address.address1}<br /></span>}
                                                        {shopifyCustomer.default_address.address2 && <span>{shopifyCustomer.default_address.address2}<br /></span>}
                                                        {shopifyCustomer.default_address.city && <span>{shopifyCustomer.default_address.city}, </span>}
                                                        {shopifyCustomer.default_address.province && <span>{shopifyCustomer.default_address.province} </span>}
                                                        {shopifyCustomer.default_address.zip && <span>{shopifyCustomer.default_address.zip}<br /></span>}
                                                        {shopifyCustomer.default_address.country && <span>{shopifyCustomer.default_address.country}</span>}
                                                        {shopifyCustomer.default_address.phone && (
                                                            <span className="block mt-2 text-gray-600">Phone: {shopifyCustomer.default_address.phone}</span>
                                                        )}
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                        {shopifyCustomer.tags && (
                                            <div>
                                                <h4 className="text-sm font-semibold text-gray-700 mb-2">Tags</h4>
                                                <div className="flex flex-wrap gap-2">
                                                    {shopifyCustomer.tags.split(',').map((tag, idx) => (
                                                        <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">{tag.trim()}</span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        {shopifyCustomer.note && (
                                            <div>
                                                <h4 className="text-sm font-semibold text-gray-700 mb-2">Notes</h4>
                                                <div className="bg-yellow-50 rounded-lg p-4 text-sm text-gray-700">{shopifyCustomer.note}</div>
                                            </div>
                                        )}
                                        <div className="pt-4 border-t border-gray-200">
                                            <div className="flex justify-between items-center text-xs text-gray-400">
                                                <span>Shopify ID: {shopifyCustomer.shopify_customer_id}</span>
                                                {shopifyCustomer.shopify_created_at && (
                                                    <span>Customer since: {safeFormatDate(shopifyCustomer.shopify_created_at, 'PPP', 'Unknown')}</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ) : lead.shopify_customer_id ? (
                                    <div className="text-center py-12">
                                        <UserCircle className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                                        <p className="text-gray-600 font-medium">Customer data not synced yet</p>
                                        <p className="text-sm text-gray-500 mt-1">Shopify Customer ID: <span className="font-mono">{lead.shopify_customer_id}</span></p>
                                        <button
                                            onClick={handleSyncShopifyCustomer}
                                            disabled={isSyncingCustomer}
                                            className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                                        >
                                            <Download className={`h-4 w-4 ${isSyncingCustomer ? 'animate-pulse' : ''}`} />
                                            <span>{isSyncingCustomer ? 'Fetching...' : 'Fetch from Shopify'}</span>
                                        </button>
                                    </div>
                                ) : (
                                    <div className="text-center py-12 text-gray-500">
                                        <UserCircle className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                                        <p>No Shopify customer linked to this lead</p>
                                        <p className="text-xs text-gray-400 mt-2">Click below to search and link a customer by phone number</p>
                                        <button
                                            onClick={handleFindAndLinkCustomer}
                                            disabled={isSyncingCustomer}
                                            className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                                        >
                                            <RefreshCw className={`h-4 w-4 ${isSyncingCustomer ? 'animate-spin' : ''}`} />
                                            {isSyncingCustomer ? 'Searching...' : 'Find & Link Customer'}
                                        </button>
                                    </div>
                                )}
                                </div>
                            </div>

                            {/* ── RIGHT: Customer Profiling ── */}
                            <div className="w-1/2 flex flex-col overflow-y-auto">
                                <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-6 py-4 border-b border-green-100 sticky top-0 z-10">
                                    <h3 className="text-lg font-semibold text-gray-800">Customer Profile</h3>
                                    <p className="text-xs text-gray-500 mt-0.5">Manually Filled By Agent</p>
                                </div>
                                <div className="p-6 space-y-5">
                                    {/* Row 1: Gender */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Gender</label>
                                        <div className="flex gap-3">
                                            {['Male', 'Female', 'Other'].map(opt => (
                                                <button
                                                    key={opt}
                                                    type="button"
                                                    onClick={() => setCustomerProfile(prev => ({ ...prev, gender: prev.gender === opt ? '' : opt }))}
                                                    className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                                                        customerProfile.gender === opt
                                                            ? 'bg-brandColor text-white border-brandColor'
                                                            : 'bg-white text-gray-600 border-gray-300 hover:border-brandColor'
                                                    }`}
                                                >
                                                    {opt}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Row 2: DOB + Age */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1.5">Date of Birth</label>
                                            <input
                                                type="date"
                                                value={customerProfile.dob}
                                                onChange={e => {
                                                    const dob = e.target.value;
                                                    let age = '';
                                                    if (dob) {
                                                        const today = new Date();
                                                        const birth = new Date(dob);
                                                        let a = today.getFullYear() - birth.getFullYear();
                                                        if (
                                                            today.getMonth() < birth.getMonth() ||
                                                            (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())
                                                        ) a--;
                                                        age = String(a);
                                                    }
                                                    setCustomerProfile(prev => ({ ...prev, dob, age }));
                                                }}
                                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brandColor"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1.5">Age</label>
                                            <input
                                                type="number"
                                                min={0}
                                                max={120}
                                                placeholder="e.g. 32"
                                                value={customerProfile.age}
                                                onChange={e => setCustomerProfile(prev => ({ ...prev, age: e.target.value, dob: '' }))}
                                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brandColor"
                                            />
                                        </div>
                                    </div>

                                    {/* Row 3: Health Issues */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Existing Health Issues</label>
                                        <textarea
                                            rows={2}
                                            placeholder="e.g. Thyroid, Diabetes, BP..."
                                            value={customerProfile.health_issues}
                                            onChange={e => setCustomerProfile(prev => ({ ...prev, health_issues: e.target.value }))}
                                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brandColor resize-none"
                                        />
                                    </div>

                                    {/* Row 4: Height + Weight */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1.5">Height</label>
                                            <input
                                                type="text"
                                                placeholder="e.g. 165 cm"
                                                value={customerProfile.height_cm}
                                                onChange={e => setCustomerProfile(prev => ({ ...prev, height_cm: e.target.value }))}
                                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brandColor"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1.5">Body Weight</label>
                                            <input
                                                type="text"
                                                placeholder="e.g. 70 kg"
                                                value={customerProfile.weight_kg}
                                                onChange={e => setCustomerProfile(prev => ({ ...prev, weight_kg: e.target.value }))}
                                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brandColor"
                                            />
                                        </div>
                                    </div>

                                    {/* Row 5: Primary Goal */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Primary Goal</label>
                                        <textarea
                                            rows={2}
                                            placeholder="e.g. Weight loss, muscle gain, managing diabetes..."
                                            value={customerProfile.primary_goal}
                                            onChange={e => setCustomerProfile(prev => ({ ...prev, primary_goal: e.target.value }))}
                                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brandColor resize-none"
                                        />
                                    </div>

                                    {/* Row 6: Family Medical History */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Family Medical History</label>
                                        <textarea
                                            rows={2}
                                            placeholder="e.g. Hereditary diabetes, heart disease..."
                                            value={customerProfile.family_medical_history}
                                            onChange={e => setCustomerProfile(prev => ({ ...prev, family_medical_history: e.target.value }))}
                                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brandColor resize-none"
                                        />
                                    </div>

                                    {/* Save Button */}
                                    <div className="flex justify-start pt-2">
                                        <button
                                            onClick={handleSaveProfile}
                                            disabled={isSavingProfile}
                                            className="flex items-center gap-2 px-5 py-2 bg-brandColor text-white text-sm font-medium rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                                        >
                                            {isSavingProfile ? (
                                                <>
                                                    <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                                    Saving...
                                                </>
                                            ) : 'Save Profile'}
                                        </button>
                                    </div>
                                </div>
                            </div>

                        </div>
                    )}

                    {/* Checkout History Tab */}
                    {activeTab === 'checkouthistory' && isEcommerce && (
                        <div>
                            <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-6 py-4 border-b border-green-100">
                                <h3 className="text-lg font-semibold text-gray-800">Checkout History</h3>
                            </div>
                            <div className="p-6">
                            {loadingTabs.has('checkouthistory') ? (
                                <div className="flex items-center justify-center py-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brandColor"></div>
                                    <span className="ml-2 text-gray-600">Loading checkout history...</span>
                                </div>
                            ) : checkoutHistory.length === 0 ? (
                                <div className="text-center py-12 text-gray-500">
                                    <History className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                                    <p>No checkout history found</p>
                                    <p className="text-xs text-gray-400 mt-2">Checkouts will appear here when customer abandons carts</p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <p className="text-sm text-gray-500 mb-4">{checkoutHistory.length} checkout(s) found</p>
                                    <div className="space-y-4">
                                        {checkoutHistory.map((checkout) => (
                                            <div key={checkout.uuid} className="bg-white border rounded-lg shadow-sm hover:shadow-md transition-shadow">
                                                {/* Checkout Header */}
                                                <div className="p-4 border-b border-gray-100">
                                                    <div className="flex items-start justify-between">
                                                        <div>
                                                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                                <span className="font-semibold text-gray-900">
                                                                    Checkout #{checkout.checkout_id || '-'}
                                                                </span>
                                                                {checkout.source === 'gokwik_log' && (
                                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                                                                        GoKwik
                                                                    </span>
                                                                )}
                                                                {checkout.isConverted ? (
                                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                                        ✓ Order {checkout.order_id}
                                                                    </span>
                                                                ) : (
                                                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                                                        checkout.statusDisplay?.toLowerCase().includes('order')
                                                                            ? 'bg-green-100 text-green-800'
                                                                            : 'bg-yellow-100 text-yellow-800'
                                                                    }`}>
                                                                        {checkout.statusDisplay || 'Abandoned'}
                                                                    </span>
                                                                )}
                                                                {checkout.drop_stage && (
                                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                                                        {checkout.drop_stage}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="text-xs text-gray-500">
                                                                {safeFormatDate(checkout.checkout_date || checkout.created_date, 'PPp', 'N/A')}
                                                            </div>
                                                        </div>
                                                        <div className="text-right">
                                                            <div className="text-lg font-bold text-gray-900">
                                                                {checkout.currency || '₹'} {Number(checkout.cart_value || 0).toFixed(2)}
                                                            </div>
                                                            <div className="text-xs text-gray-500">
                                                                {checkout.items_count || checkout.line_items?.length || 0} item(s)
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {checkout.checkout_url && (
                                                        <a
                                                            href={checkout.checkout_url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="inline-flex items-center gap-1 mt-2 text-xs text-brandColor hover:underline"
                                                        >
                                                            View checkout →
                                                        </a>
                                                    )}
                                                </div>

                                                {/* Line Items */}
                                                {checkout.line_items && checkout.line_items.length > 0 && (
                                                    <div className="p-4 bg-gray-50">
                                                        <div className="text-xs font-medium text-gray-500 uppercase mb-2">Line Items</div>
                                                        <div className="space-y-2">
                                                            {checkout.line_items.map((item, idx) => (
                                                                <div key={idx} className="flex items-center justify-between bg-white rounded p-2 text-sm">
                                                                    <div className="flex-1">
                                                                        <div className="font-medium text-gray-900">{item.title}</div>
                                                                        {item.variant_title && (
                                                                            <div className="text-xs text-gray-500">{item.variant_title}</div>
                                                                        )}
                                                                        {item.sku && (
                                                                            <div className="text-xs text-gray-400 font-mono">SKU: {item.sku}</div>
                                                                        )}
                                                                    </div>
                                                                    <div className="text-right ml-4">
                                                                        <div className="text-gray-600">x{item.quantity}</div>
                                                                        <div className="font-medium text-gray-900">
                                                                            {checkout.currency || '₹'} {Number(item.total || item.price * item.quantity).toFixed(2)}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            </div>
                        </div>
                    )}

                    {/* Order History Tab */}
                    {activeTab === 'orderhistory' && isEcommerce && (
                        <div>
                            <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-6 py-4 border-b border-green-100 flex items-center justify-between">
                                <h3 className="text-lg font-semibold text-gray-800">Order History</h3>
                                {(lead?.shopify_customer_id || shopifyCustomer?.shopify_customer_id) && (
                                    <button
                                        onClick={handleSyncOrders}
                                        disabled={isSyncingOrders}
                                        className="flex items-center gap-2 px-3 py-1.5 text-sm text-green-600 hover:text-green-700 hover:bg-green-100 rounded-lg transition-colors disabled:opacity-50"
                                        title="Sync past 6 months orders from Shopify"
                                    >
                                        <RefreshCw className={`h-4 w-4 ${isSyncingOrders ? 'animate-spin' : ''}`} />
                                        <span>{isSyncingOrders ? 'Syncing...' : 'Sync from Shopify'}</span>
                                    </button>
                                )}
                            </div>
                            <div className="p-6">
                            {loadingTabs.has('orderhistory') ? (
                                <div className="flex items-center justify-center py-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brandColor"></div>
                                    <span className="ml-2 text-gray-600">Loading order history...</span>
                                </div>
                            ) : orderHistory.length === 0 ? (
                                <div className="text-center py-12 text-gray-500">
                                    <Package className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                                    <p>No orders found</p>
                                    <p className="text-xs text-gray-400 mt-2">Orders will appear here when customer completes purchases</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 gap-4">
                                    {/* Draft Orders Section */}
                                    <div>
                                        <div className="flex items-center gap-2 mb-3 px-1">
                                            <div className="h-px flex-1 bg-orange-200"></div>
                                            <span className="text-xs font-semibold text-orange-600 uppercase tracking-wide px-2">
                                                Draft Orders ({orderHistory.filter(o => o.order_type === 'draft').length})
                                            </span>
                                            <div className="h-px flex-1 bg-orange-200"></div>
                                        </div>
                                        <div className="space-y-3 max-h-[70vh] overflow-y-auto">
                                            {orderHistory.filter(o => o.order_type === 'draft').length === 0 ? (
                                                <div className="text-center py-8 text-gray-400 text-sm">No draft orders</div>
                                            ) : orderHistory
                                                .filter(o => o.order_type === 'draft')
                                                .sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime())
                                                .map((order) => (
                                            <div key={order.uuid || order.id || order.shopify_order_id} className="bg-white border rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-hidden">
                                                {/* Header */}
                                                <div className="p-3 bg-gray-50 border-b flex items-center justify-between flex-wrap gap-2">
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-semibold text-gray-900">
                                                            {order.order_name || order.order_number || '-'}
                                                        </span>
                                                        {order.order_type === 'draft' && (
                                                            <span className="px-1.5 py-0.5 text-xs font-medium bg-orange-100 text-orange-700 rounded">Draft</span>
                                                        )}
                                                        {order.uuid && (
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); handleRefreshOrder(order.uuid!); }}
                                                                disabled={refreshingOrderId === order.uuid}
                                                                className="px-2 py-0.5 text-xs font-medium text-green-700 bg-green-100 hover:bg-green-200 rounded-full transition-colors disabled:opacity-50 flex items-center gap-1"
                                                            >
                                                                {refreshingOrderId === order.uuid ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
                                                                Sync
                                                            </button>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-1 flex-wrap">
                                                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                                            order.financial_status === 'paid' ? 'bg-green-100 text-green-800' :
                                                            order.financial_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                                            order.financial_status === 'voided' ? 'bg-red-100 text-red-800' :
                                                            order.financial_status === 'partially_paid' ? 'bg-orange-100 text-orange-800' :
                                                            'bg-gray-100 text-gray-700'
                                                        }`}>{order.financial_status || 'Unknown'}</span>
                                                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                                            order.source_name === 'razorpay' ? 'bg-indigo-100 text-indigo-800' :
                                                            order.source_name === 'cod' ? 'bg-amber-100 text-amber-800' :
                                                            order.source_name === 'checkout_link' ? 'bg-cyan-100 text-cyan-800' :
                                                            'bg-gray-100 text-gray-700'
                                                        }`}>{order.source_name === 'razorpay' ? 'Razorpay' : order.source_name === 'cod' ? 'COD' : order.source_name === 'checkout_link' ? 'Checkout' : order.source_name || 'N/A'}</span>
                                                    </div>
                                                </div>

                                                {/* Two Column Layout: Order Details | Line Items */}
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-0 divide-y md:divide-y-0 md:divide-x">
                                                    {/* Left: Order Details */}
                                                    <div className="p-3">
                                                        <div className="grid grid-cols-2 gap-3 text-sm">
                                                            <div>
                                                                <span className="text-xs text-gray-500 block">Date</span>
                                                                <p className="font-medium">{safeFormatDate(order.order_date || order.processed_at || order.created_at, 'PP, p', 'N/A')}</p>
                                                            </div>
                                                            <div>
                                                                <span className="text-xs text-gray-500 block">Total</span>
                                                                <p className="font-bold text-green-600">₹{Number(order.total_price || 0).toLocaleString('en-IN')}</p>
                                                            </div>
                                                            <div>
                                                                <span className="text-xs text-gray-500 block">Shopify ID</span>
                                                                <div className="flex items-center gap-1">
                                                                    <span className="font-mono text-xs">{order.shopify_order_id}</span>
                                                                    <a href={`https://admin.shopify.com/store/zero-harm/orders/${order.shopify_order_id}`} target="_blank" rel="noopener noreferrer" className="text-blue-600"><ExternalLink size={10} /></a>
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <span className="text-xs text-gray-500 block">Type</span>
                                                                <p className="font-medium capitalize">{order.order_type === 'draft' ? 'Draft' : 'Completed'}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {/* Right: Line Items */}
                                                    <div className="p-3 bg-gray-50">
                                                        <span className="text-xs text-gray-500 block mb-2">Line Items ({order.lineItems?.length || 0})</span>
                                                        {order.lineItems && order.lineItems.length > 0 ? (
                                                            <div className="space-y-1 max-h-24 overflow-y-auto">
                                                                {order.lineItems.map((item: any, idx: number) => (
                                                                    <div key={idx} className="flex justify-between text-xs">
                                                                        <span className="truncate flex-1">{item.title} {item.variant_title && <span className="text-gray-400">({item.variant_title})</span>}</span>
                                                                        <span className="ml-2 font-medium">x{item.quantity} ₹{Number(item.price || 0).toFixed(0)}</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <p className="text-xs text-gray-400 italic">No items</p>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Payment Section - Conditional based on source */}
                                                <div className="p-3 border-t">
                                                    {/* Razorpay Payment Details */}
                                                    {order.source_name === 'razorpay' && (
                                                        <div className="p-3 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-200">
                                                            <div className="flex items-center justify-between mb-2">
                                                                <span className="text-sm font-semibold text-indigo-800">Razorpay Payment</span>
                                                            </div>
                                                            <div className="grid grid-cols-3 gap-2 mb-2">
                                                                <div className="bg-white p-2 rounded text-center">
                                                                    <span className="text-xs text-gray-500 block">Total</span>
                                                                    <p className="font-bold text-gray-900 text-sm">₹{Number(order.total_price || 0).toLocaleString('en-IN')}</p>
                                                                </div>
                                                                <div className="bg-white p-2 rounded text-center">
                                                                    <span className="text-xs text-gray-500 block">Paid</span>
                                                                    <p className="font-bold text-green-600 text-sm">₹{((order.latest_payment_link?.amount_paid || 0) / 100).toLocaleString('en-IN')}</p>
                                                                </div>
                                                                <div className="bg-white p-2 rounded text-center">
                                                                    <span className="text-xs text-gray-500 block">Balance</span>
                                                                    <p className="font-bold text-orange-600 text-sm">₹{Math.max(0, Number(order.total_price || 0) - ((order.latest_payment_link?.amount_paid || 0) / 100)).toLocaleString('en-IN')}</p>
                                                                </div>
                                                            </div>
                                                            {order.latest_payment_link && (
                                                                <div className="bg-white p-2 rounded text-xs space-y-1">
                                                                    {order.latest_payment_link.razorpay_payment_id && (
                                                                        <div className="flex justify-between"><span className="text-gray-500">Payment ID:</span><span className="font-mono text-gray-700">{order.latest_payment_link.razorpay_payment_id}</span></div>
                                                                    )}
                                                                    {order.latest_payment_link.payment_method && (
                                                                        <div className="flex justify-between"><span className="text-gray-500">Method:</span><span className="font-medium capitalize">{order.latest_payment_link.payment_method}</span></div>
                                                                    )}
                                                                    {order.latest_payment_link.paid_at && (
                                                                        <div className="flex justify-between"><span className="text-gray-500">Paid At:</span><span>{safeFormatDate(order.latest_payment_link.paid_at, 'PP p', 'N/A')}</span></div>
                                                                    )}
                                                                    {order.latest_payment_link.short_url && (
                                                                        <div className="flex justify-between items-center pt-1 border-t">
                                                                            <span className="text-gray-500">Link:</span>
                                                                            <a href={order.latest_payment_link.short_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">Open <ExternalLink size={10} /></a>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}

                                                    {/* COD Payment Section */}
                                                    {order.source_name === 'cod' && (
                                                        <div className="p-3 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg border border-amber-200">
                                                            <div className="flex items-center justify-between">
                                                                <span className="text-sm font-semibold text-amber-800">Cash on Delivery</span>
                                                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                                                    order.financial_status === 'paid' ? 'bg-green-200 text-green-800' : 'bg-amber-200 text-amber-800'
                                                                }`}>{order.financial_status === 'paid' ? 'Collected' : 'Pending Collection'}</span>
                                                            </div>
                                                            <div className="mt-2 text-sm">
                                                                <span className="text-gray-600">Amount to Collect: </span>
                                                                <span className="font-bold text-amber-700">₹{Number(order.total_price || 0).toLocaleString('en-IN')}</span>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Checkout Link Section */}
                                                    {order.source_name === 'checkout_link' && (
                                                        <div className="p-3 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-lg border border-cyan-200">
                                                            <div className="flex items-center justify-between">
                                                                <span className="text-sm font-semibold text-cyan-800">Checkout Link Order</span>
                                                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                                                    order.financial_status === 'paid' ? 'bg-green-200 text-green-800' : 'bg-yellow-200 text-yellow-800'
                                                                }`}>{order.financial_status || 'Pending'}</span>
                                                            </div>
                                                            <div className="mt-2 text-sm">
                                                                <span className="text-gray-600">Total: </span>
                                                                <span className="font-bold text-cyan-700">₹{Number(order.total_price || 0).toLocaleString('en-IN')}</span>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Default/Other source */}
                                                    {!['razorpay', 'cod', 'checkout_link'].includes(order.source_name || '') && (
                                                        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                                                            <div className="flex items-center justify-between">
                                                                <span className="text-sm font-semibold text-gray-700">{order.source_name || 'Standard Order'}</span>
                                                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                                                    order.financial_status === 'paid' ? 'bg-green-200 text-green-800' : 'bg-gray-200 text-gray-700'
                                                                }`}>{order.financial_status || 'Unknown'}</span>
                                                            </div>
                                                            <div className="mt-2 text-sm">
                                                                <span className="text-gray-600">Total: </span>
                                                                <span className="font-bold">₹{Number(order.total_price || 0).toLocaleString('en-IN')}</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Shipping & Tracking Section */}
                                                <div className="p-3 border-t">
                                                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <Truck size={16} className="text-blue-600" />
                                                            <span className="text-sm font-medium text-blue-800">Shipping & Tracking</span>
                                                            <div className="ml-auto flex items-center gap-1.5">
                                                                {/* Fulfillment badge */}
                                                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                                                    order.fulfillment_status === 'fulfilled' ? 'bg-green-200 text-green-800' :
                                                                    order.fulfillment_status === 'partial' ? 'bg-yellow-200 text-yellow-800' :
                                                                    order.cancelled_at ? 'bg-red-200 text-red-800' :
                                                                    'bg-gray-200 text-gray-700'
                                                                }`}>
                                                                    {order.cancelled_at ? 'Cancelled' :
                                                                     order.fulfillment_status === 'fulfilled' ? 'Shipped' :
                                                                     order.fulfillment_status === 'partial' ? 'Partially Shipped' :
                                                                     'Not Shipped'}
                                                                </span>
                                                                {/* Clickpost delivery badge */}
                                                                {order.latest_delivery_status != null && (
                                                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${getDeliveryStatusBadge(order.latest_delivery_status).className}`}>
                                                                        {getDeliveryStatusBadge(order.latest_delivery_status).label}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        {/* Clickpost delivery detail line */}
                                                        {order.latest_delivery_status != null && (
                                                            <div className="mb-2 flex items-center gap-1.5 text-xs text-gray-600">
                                                                <span className="font-medium">Delivery:</span>
                                                                <span>{order.latest_delivery_status_desc || getDeliveryStatusBadge(order.latest_delivery_status).label}</span>
                                                                {order.latest_delivery_location && <span>· {order.latest_delivery_location}</span>}
                                                                {order.latest_delivery_event_at && (
                                                                    <span>· {new Date(order.latest_delivery_event_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                                                                )}
                                                            </div>
                                                        )}
                                                        {order.tracking_info && order.tracking_info.tracking_numbers && order.tracking_info.tracking_numbers.length > 0 ? (
                                                            <div className="space-y-1">
                                                                {order.tracking_info.tracking_company && (
                                                                    <p className="text-xs text-gray-500 mb-1">Carrier: <span className="font-medium">{order.tracking_info.tracking_company}</span></p>
                                                                )}
                                                                {order.tracking_info.tracking_numbers.map((trackingNo: string, idx: number) => (
                                                                    <div key={idx} className="flex items-center justify-between bg-white p-2 rounded">
                                                                        <span className="text-sm font-mono text-gray-700">{trackingNo}</span>
                                                                        {order.tracking_info?.tracking_urls?.[idx] && (
                                                                            <a href={order.tracking_info.tracking_urls[idx]} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-2 py-1 text-xs text-white bg-blue-600 hover:bg-blue-700 rounded">
                                                                                Track <ExternalLink size={10} />
                                                                            </a>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <p className="text-sm text-gray-500 italic">
                                                                {order.cancelled_at ? 'Order was cancelled' :
                                                                 order.fulfillment_status ? 'Tracking info not available yet' :
                                                                 'Awaiting shipment'}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Cancelled Notice */}
                                                {order.cancelled_at && (
                                                    <div className="mx-3 mb-3 p-2 bg-red-50 rounded text-sm text-red-700 border border-red-200">
                                                        <strong>Cancelled:</strong> {safeFormatDate(order.cancelled_at, 'PPp', 'Unknown')}
                                                        {order.cancel_reason && <span> - {order.cancel_reason}</span>}
                                                    </div>
                                                )}

                                                {/* Tags */}
                                                {order.tags && order.tags.trim() !== '' && (
                                                    <div className="px-3 pb-3 flex flex-wrap gap-1.5">
                                                        {order.tags.split(',').map((tag: string, idx: number) => (
                                                            <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                                                {tag.trim()}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                        </div>
                                    </div>

                                    {/* Completed Orders Section */}
                                    <div>
                                        <div className="flex items-center gap-2 mb-3 px-1">
                                            <div className="h-px flex-1 bg-green-200"></div>
                                            <span className="text-xs font-semibold text-green-600 uppercase tracking-wide px-2">
                                                Completed Orders ({orderHistory.filter(o => o.order_type !== 'draft').length})
                                            </span>
                                            <div className="h-px flex-1 bg-green-200"></div>
                                        </div>
                                        <div className="space-y-3 max-h-[70vh] overflow-y-auto">
                                            {orderHistory.filter(o => o.order_type === 'order' || (o.order_type && o.order_type !== 'draft')).length === 0 ? (
                                                <div className="text-center py-8 text-gray-400 text-sm">No completed orders</div>
                                            ) : orderHistory
                                                .filter(o => o.order_type === 'order' || (o.order_type && o.order_type !== 'draft'))
                                                .sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime())
                                                .map((order) => (
                                            <div key={order.uuid || order.id || order.shopify_order_id} className="bg-white border rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-hidden">
                                                {/* Header */}
                                                <div className="p-3 bg-gray-50 border-b flex items-center justify-between flex-wrap gap-2">
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-semibold text-gray-900">
                                                            {order.order_name || order.order_number || '-'}
                                                        </span>
                                                        {order.uuid && (
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); handleRefreshOrder(order.uuid!); }}
                                                                disabled={refreshingOrderId === order.uuid}
                                                                className="px-2 py-0.5 text-xs font-medium text-green-700 bg-green-100 hover:bg-green-200 rounded-full transition-colors disabled:opacity-50 flex items-center gap-1"
                                                            >
                                                                {refreshingOrderId === order.uuid ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
                                                                Sync
                                                            </button>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-1 flex-wrap">
                                                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                                            order.financial_status === 'paid' ? 'bg-green-100 text-green-800' :
                                                            order.financial_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                                            order.financial_status === 'voided' ? 'bg-red-100 text-red-800' :
                                                            order.financial_status === 'partially_paid' ? 'bg-orange-100 text-orange-800' :
                                                            'bg-gray-100 text-gray-700'
                                                        }`}>{order.financial_status || 'Unknown'}</span>
                                                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                                            order.source_name === 'razorpay' ? 'bg-indigo-100 text-indigo-800' :
                                                            order.source_name === 'cod' ? 'bg-amber-100 text-amber-800' :
                                                            order.source_name === 'checkout_link' ? 'bg-cyan-100 text-cyan-800' :
                                                            'bg-gray-100 text-gray-700'
                                                        }`}>{order.source_name === 'razorpay' ? 'Razorpay' : order.source_name === 'cod' ? 'COD' : order.source_name === 'checkout_link' ? 'Checkout' : order.source_name || 'N/A'}</span>
                                                    </div>
                                                </div>

                                                {/* Two Column Layout: Order Details | Line Items */}
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-0 divide-y md:divide-y-0 md:divide-x">
                                                    {/* Left: Order Details */}
                                                    <div className="p-3">
                                                        <div className="grid grid-cols-2 gap-3 text-sm">
                                                            <div>
                                                                <span className="text-xs text-gray-500 block">Date</span>
                                                                <p className="font-medium">{safeFormatDate(order.order_date || order.processed_at || order.created_at, 'PP, p', 'N/A')}</p>
                                                            </div>
                                                            <div>
                                                                <span className="text-xs text-gray-500 block">Total</span>
                                                                <p className="font-bold text-green-600">₹{Number(order.total_price || 0).toLocaleString('en-IN')}</p>
                                                            </div>
                                                            <div>
                                                                <span className="text-xs text-gray-500 block">Shopify ID</span>
                                                                <div className="flex items-center gap-1">
                                                                    <span className="font-mono text-xs">{order.shopify_order_id}</span>
                                                                    <a href={`https://admin.shopify.com/store/zero-harm/orders/${order.shopify_order_id}`} target="_blank" rel="noopener noreferrer" className="text-blue-600"><ExternalLink size={10} /></a>
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <span className="text-xs text-gray-500 block">Type</span>
                                                                <p className="font-medium capitalize">Completed</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {/* Right: Line Items */}
                                                    <div className="p-3 bg-gray-50">
                                                        <div className="text-xs font-medium text-gray-500 uppercase mb-2">Line Items ({order.lineItems?.length || 0})</div>
                                                        <div className="space-y-1 max-h-32 overflow-y-auto">
                                                            {order.lineItems && order.lineItems.length > 0 ? (
                                                                order.lineItems.map((item: any, idx: number) => (
                                                                    <div key={idx} className="flex justify-between text-xs bg-white p-1.5 rounded">
                                                                        <span className="truncate flex-1 mr-2">{item.title} {item.variant_title && `(${item.variant_title})`}</span>
                                                                        <span className="text-gray-600 whitespace-nowrap">x{item.quantity} = ₹{Number(item.total_price || item.total || 0).toLocaleString('en-IN')}</span>
                                                                    </div>
                                                                ))
                                                            ) : (
                                                                <p className="text-xs text-gray-400 italic">No line items</p>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Payment Section - Conditional based on source */}
                                                <div className="p-3 border-t">
                                                    {/* Razorpay Payment Details */}
                                                    {order.source_name === 'razorpay' && (
                                                        <div className="p-3 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-200">
                                                            <div className="flex items-center justify-between mb-2">
                                                                <span className="text-sm font-semibold text-indigo-800">Razorpay Payment</span>
                                                            </div>
                                                            <div className="grid grid-cols-3 gap-2 mb-2">
                                                                <div className="bg-white p-2 rounded text-center">
                                                                    <span className="text-xs text-gray-500 block">Total</span>
                                                                    <p className="font-bold text-gray-900 text-sm">₹{Number(order.total_price || 0).toLocaleString('en-IN')}</p>
                                                                </div>
                                                                <div className="bg-white p-2 rounded text-center">
                                                                    <span className="text-xs text-gray-500 block">Paid</span>
                                                                    <p className="font-bold text-green-600 text-sm">₹{((order.latest_payment_link?.amount_paid || 0) / 100).toLocaleString('en-IN')}</p>
                                                                </div>
                                                                <div className="bg-white p-2 rounded text-center">
                                                                    <span className="text-xs text-gray-500 block">Balance</span>
                                                                    <p className="font-bold text-orange-600 text-sm">₹{Math.max(0, Number(order.total_price || 0) - ((order.latest_payment_link?.amount_paid || 0) / 100)).toLocaleString('en-IN')}</p>
                                                                </div>
                                                            </div>
                                                            {order.latest_payment_link && (
                                                                <div className="bg-white p-2 rounded text-xs space-y-1">
                                                                    {order.latest_payment_link.razorpay_payment_id && (
                                                                        <div className="flex justify-between"><span className="text-gray-500">Payment ID:</span><span className="font-mono text-gray-700">{order.latest_payment_link.razorpay_payment_id}</span></div>
                                                                    )}
                                                                    {order.latest_payment_link.payment_method && (
                                                                        <div className="flex justify-between"><span className="text-gray-500">Method:</span><span className="font-medium capitalize">{order.latest_payment_link.payment_method}</span></div>
                                                                    )}
                                                                    {order.latest_payment_link.paid_at && (
                                                                        <div className="flex justify-between"><span className="text-gray-500">Paid At:</span><span>{safeFormatDate(order.latest_payment_link.paid_at, 'PP p', 'N/A')}</span></div>
                                                                    )}
                                                                    {order.latest_payment_link.short_url && (
                                                                        <div className="flex justify-between items-center pt-1 border-t">
                                                                            <span className="text-gray-500">Link:</span>
                                                                            <a href={order.latest_payment_link.short_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">Open <ExternalLink size={10} /></a>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}

                                                    {/* COD Payment Section */}
                                                    {order.source_name === 'cod' && (
                                                        <div className="p-3 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg border border-amber-200">
                                                            <div className="flex items-center justify-between">
                                                                <span className="text-sm font-semibold text-amber-800">Cash on Delivery</span>
                                                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                                                    order.financial_status === 'paid' ? 'bg-green-200 text-green-800' : 'bg-amber-200 text-amber-800'
                                                                }`}>{order.financial_status === 'paid' ? 'Collected' : 'Pending Collection'}</span>
                                                            </div>
                                                            <div className="mt-2 text-sm">
                                                                <span className="text-gray-600">Amount to Collect: </span>
                                                                <span className="font-bold text-amber-700">₹{Number(order.total_price || 0).toLocaleString('en-IN')}</span>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Checkout Link Section */}
                                                    {order.source_name === 'checkout_link' && (
                                                        <div className="p-3 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-lg border border-cyan-200">
                                                            <div className="flex items-center justify-between">
                                                                <span className="text-sm font-semibold text-cyan-800">Checkout Link Order</span>
                                                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                                                    order.financial_status === 'paid' ? 'bg-green-200 text-green-800' : 'bg-yellow-200 text-yellow-800'
                                                                }`}>{order.financial_status || 'Pending'}</span>
                                                            </div>
                                                            <div className="mt-2 text-sm">
                                                                <span className="text-gray-600">Total: </span>
                                                                <span className="font-bold text-cyan-700">₹{Number(order.total_price || 0).toLocaleString('en-IN')}</span>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Default/Other source */}
                                                    {!['razorpay', 'cod', 'checkout_link'].includes(order.source_name || '') && (
                                                        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                                                            <div className="flex items-center justify-between">
                                                                <span className="text-sm font-semibold text-gray-700">{order.source_name || 'Standard Order'}</span>
                                                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                                                    order.financial_status === 'paid' ? 'bg-green-200 text-green-800' : 'bg-gray-200 text-gray-700'
                                                                }`}>{order.financial_status || 'Unknown'}</span>
                                                            </div>
                                                            <div className="mt-2 text-sm">
                                                                <span className="text-gray-600">Total: </span>
                                                                <span className="font-bold">₹{Number(order.total_price || 0).toLocaleString('en-IN')}</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Shipping & Tracking Section */}
                                                <div className="p-3 border-t">
                                                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <Truck size={16} className="text-blue-600" />
                                                            <span className="text-sm font-medium text-blue-800">Shipping & Tracking</span>
                                                            <div className="ml-auto flex items-center gap-1.5">
                                                                {/* Fulfillment badge */}
                                                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                                                    order.fulfillment_status === 'fulfilled' ? 'bg-green-200 text-green-800' :
                                                                    order.fulfillment_status === 'partial' ? 'bg-yellow-200 text-yellow-800' :
                                                                    order.cancelled_at ? 'bg-red-200 text-red-800' :
                                                                    'bg-gray-200 text-gray-700'
                                                                }`}>
                                                                    {order.cancelled_at ? 'Cancelled' :
                                                                     order.fulfillment_status === 'fulfilled' ? 'Shipped' :
                                                                     order.fulfillment_status === 'partial' ? 'Partially Shipped' :
                                                                     'Not Shipped'}
                                                                </span>
                                                                {/* Clickpost delivery badge */}
                                                                {order.latest_delivery_status != null && (
                                                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${getDeliveryStatusBadge(order.latest_delivery_status).className}`}>
                                                                        {getDeliveryStatusBadge(order.latest_delivery_status).label}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        {/* Clickpost delivery detail line */}
                                                        {order.latest_delivery_status != null && (
                                                            <div className="mb-2 flex items-center gap-1.5 text-xs text-gray-600">
                                                                <span className="font-medium">Delivery:</span>
                                                                <span>{order.latest_delivery_status_desc || getDeliveryStatusBadge(order.latest_delivery_status).label}</span>
                                                                {order.latest_delivery_location && <span>· {order.latest_delivery_location}</span>}
                                                                {order.latest_delivery_event_at && (
                                                                    <span>· {new Date(order.latest_delivery_event_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                                                                )}
                                                            </div>
                                                        )}
                                                        {order.tracking_info && order.tracking_info.tracking_numbers && order.tracking_info.tracking_numbers.length > 0 ? (
                                                            <div className="space-y-1">
                                                                {order.tracking_info.tracking_company && (
                                                                    <p className="text-xs text-gray-500 mb-1">Carrier: <span className="font-medium">{order.tracking_info.tracking_company}</span></p>
                                                                )}
                                                                {order.tracking_info.tracking_numbers.map((trackingNo: string, idx: number) => (
                                                                    <div key={idx} className="flex items-center justify-between bg-white p-2 rounded">
                                                                        <span className="text-sm font-mono text-gray-700">{trackingNo}</span>
                                                                        {order.tracking_info?.tracking_urls?.[idx] && (
                                                                            <a href={order.tracking_info.tracking_urls[idx]} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-2 py-1 text-xs text-white bg-blue-600 hover:bg-blue-700 rounded">
                                                                                Track <ExternalLink size={10} />
                                                                            </a>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <p className="text-sm text-gray-500 italic">
                                                                {order.cancelled_at ? 'Order was cancelled' :
                                                                 order.fulfillment_status ? 'Tracking info not available yet' :
                                                                 'Awaiting shipment'}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Cancelled Notice */}
                                                {order.cancelled_at && (
                                                    <div className="mx-3 mb-3 p-2 bg-red-50 rounded text-sm text-red-700 border border-red-200">
                                                        <strong>Cancelled:</strong> {safeFormatDate(order.cancelled_at, 'PPp', 'Unknown')}
                                                        {order.cancel_reason && <span> - {order.cancel_reason}</span>}
                                                    </div>
                                                )}

                                                {/* Tags */}
                                                {order.tags && order.tags.trim() !== '' && (
                                                    <div className="px-3 pb-3 flex flex-wrap gap-1.5">
                                                        {order.tags.split(',').map((tag: string, idx: number) => (
                                                            <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                                                {tag.trim()}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                            </div>
                        </div>
                    )}

                    {/* Re-Orders Tab */}
                    {activeTab === 'reorders' && isEcommerce && (
                        <div>
                            <div className="bg-gradient-to-r from-orange-50 to-amber-50 px-6 py-4 border-b border-orange-100">
                                <h3 className="text-lg font-semibold text-gray-800">Re-Order Information</h3>
                            </div>
                            <div className="p-6">
                            {loadingTabs.has('reorders') ? (
                                <div className="flex items-center justify-center py-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                                </div>
                            ) : !reorderInfo ? (
                                <div className="text-center py-8 text-gray-500">
                                    <RefreshCw className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                                    <p>No reorder information available</p>
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    {/* Lead Summary Card */}
                                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Lead Summary</h4>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <span className="text-xs text-gray-500">Name</span>
                                                <p className="text-sm font-medium text-gray-900">{reorderInfo.lead_summary.name || 'N/A'}</p>
                                            </div>
                                            <div>
                                                <span className="text-xs text-gray-500">Status</span>
                                                <p className="text-sm font-medium text-gray-900">{reorderInfo.lead_summary.current_status || 'N/A'}</p>
                                            </div>
                                            <div>
                                                <span className="text-xs text-gray-500">Phone</span>
                                                <p className="text-sm font-medium text-gray-900">{reorderInfo.lead_summary.phone || 'N/A'}</p>
                                            </div>
                                            <div>
                                                <span className="text-xs text-gray-500">Total Orders</span>
                                                <p className="text-sm font-medium text-gray-900">{reorderInfo.lead_summary.total_orders_count}</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Reorder Items */}
                                    <div>
                                        <h4 className="text-sm font-semibold text-gray-700 mb-3">
                                            Why in Re-Orders ({reorderInfo.total_reorder_items} {reorderInfo.total_reorder_items === 1 ? 'item' : 'items'})
                                        </h4>
                                        {reorderInfo.reorder_items.length === 0 ? (
                                            <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg border border-gray-200">
                                                <p>No reorder items found</p>
                                                <p className="text-xs mt-1">This lead may not have any delivered orders with reorder dates</p>
                                            </div>
                                        ) : (
                                            <div className="space-y-3">
                                                {reorderInfo.reorder_items.map((item, idx) => (
                                                    <div key={idx} className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                                                        <div className="flex items-start justify-between mb-2">
                                                            <div className="flex-1">
                                                                <h5 className="font-medium text-gray-900">{item.title}</h5>
                                                                {item.variant_title && (
                                                                    <span className="text-xs text-gray-500">{item.variant_title}</span>
                                                                )}
                                                            </div>
                                                            {item.is_overdue ? (
                                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                                    {item.days_overdue} {item.days_overdue === 1 ? 'day' : 'days'} overdue
                                                                </span>
                                                            ) : item.days_overdue === 0 ? (
                                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                                                    Due today
                                                                </span>
                                                            ) : (
                                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                                    Due in {Math.abs(item.days_overdue)} {Math.abs(item.days_overdue) === 1 ? 'day' : 'days'}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="text-sm text-gray-600 mb-2">
                                                            Qty: {item.quantity}
                                                        </div>
                                                        <div className="border-t border-gray-100 pt-2 mt-2">
                                                            <div className="grid grid-cols-2 gap-2 text-xs">
                                                                <div>
                                                                    <span className="text-gray-500">Order:</span>
                                                                    <span className="ml-1 font-medium text-gray-700">#{item.order.order_number}</span>
                                                                </div>
                                                                <div>
                                                                    <span className="text-gray-500">Delivered:</span>
                                                                    <span className="ml-1 font-medium text-gray-700">
                                                                        {safeFormatDate(item.delivered_at, 'MMM d, yyyy', 'N/A')}
                                                                    </span>
                                                                </div>
                                                                <div>
                                                                    <span className="text-gray-500">Reorder Cycle:</span>
                                                                    <span className="ml-1 font-medium text-gray-700">{item.reorder_days} days</span>
                                                                </div>
                                                                <div>
                                                                    <span className="text-gray-500">Due:</span>
                                                                    <span className="ml-1 font-medium text-gray-700">
                                                                        {safeFormatDate(item.reorder_due_date, 'MMM d, yyyy', 'N/A')}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                            </div>
                        </div>
                    )}

                    {/* Lead Form Tab */}
                    {activeTab === 'form' && (
                        <div>
                            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-blue-100">
                                <h3 className="text-lg font-semibold text-gray-800">Lead Details</h3>
                            </div>
                            <div className="p-6">
                                <div className="w-full">
                                    <DynamicLeadForm
                                        mode="update"
                                        leadUuid={lead.uuid}
                                        activeUsersForDropdown={activeUsersForDropdown}
                                        onSubmitSuccess={() => {
                                            onUpdate?.();
                                            onClose();
                                        }}
                                        onClose={onClose}
                                        refreshData={onUpdate}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Marketing Data Tab */}
                    {activeTab === 'marketing' && (
                        <div>
                            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                                <h3 className="text-lg font-semibold text-gray-800">Marketing Attribution</h3>
                            </div>
                            <div className="p-4">
                            {loadingTabs.has('marketing') ? (
                                <div className="flex items-center justify-center py-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brandColor"></div>
                                    <span className="ml-2 text-gray-600">Loading marketing data...</span>
                                </div>
                            ) : (
                                <>
                                <div className="space-y-4">
                                {/* UTM Parameters Card */}
                                {(lead.utm_source || lead.utm_campaign || lead.utm_medium) && (
                                    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                                        <div className="flex items-center gap-3 px-4 py-3 bg-purple-50 border-b border-gray-200">
                                            <div className="p-2 bg-purple-500 rounded-lg">
                                                <Target size={16} className="text-white" />
                                            </div>
                                            <h4 className="font-semibold text-gray-800">UTM Parameters</h4>
                                        </div>
                                        <div className="p-4">
                                            <div className="grid grid-cols-2 gap-3">
                                                {[
                                                    { label: 'Source', value: lead.utm_source },
                                                    { label: 'Campaign', value: lead.utm_campaign },
                                                    { label: 'Medium', value: lead.utm_medium },
                                                    { label: 'Content', value: lead.utm_content },
                                                    { label: 'Term', value: lead.utm_term },
                                                    { label: 'Location', value: lead.utm_location },
                                                    { label: 'Device', value: lead.utm_device },
                                                    { label: 'Network', value: lead.utm_network },
                                                    { label: 'Platform', value: lead.utm_platform },
                                                    { label: 'Creative', value: lead.utm_creative },
                                                    { label: 'Ad Position', value: lead.utm_adposition }
                                                ].filter(item => item.value).map(({ label, value }) => (
                                                    <div key={label} className="bg-gray-50 rounded-lg p-3">
                                                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</span>
                                                        <p className="text-sm font-semibold text-gray-800 mt-1 truncate" title={value}>{value}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Facebook Data Card */}
                                {(lead.fb_campaign_name || lead.fb_ad_name || lead.fb_adset_name) && (
                                    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                                        <div className="flex items-center gap-3 px-4 py-3 bg-blue-50 border-b border-gray-200">
                                            <div className="p-2 bg-blue-600 rounded-lg">
                                                <Megaphone size={16} className="text-white" />
                                            </div>
                                            <h4 className="font-semibold text-gray-800">Facebook / Meta Ads</h4>
                                            {lead.fb_is_organic === 'true' && (
                                                <span className="ml-auto px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">Organic</span>
                                            )}
                                        </div>
                                        <div className="p-4">
                                            <div className="grid grid-cols-2 gap-3">
                                                {[
                                                    { label: 'Campaign', value: lead.fb_campaign_name },
                                                    { label: 'Ad Set', value: lead.fb_adset_name },
                                                    { label: 'Ad Name', value: lead.fb_ad_name },
                                                    { label: 'Platform', value: lead.fb_platform }
                                                ].filter(item => item.value).map(({ label, value }) => (
                                                    <div key={label} className="bg-gray-50 rounded-lg p-3">
                                                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</span>
                                                        <p className="text-sm font-semibold text-gray-800 mt-1 truncate" title={value}>{value}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* LinkedIn Data Card */}
                                {(lead.li_campaign_name || lead.li_form_name) && (
                                    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                                        <div className="flex items-center gap-3 px-4 py-3 bg-sky-50 border-b border-gray-200">
                                            <div className="p-2 bg-sky-600 rounded-lg">
                                                <Linkedin size={16} className="text-white" />
                                            </div>
                                            <h4 className="font-semibold text-gray-800">LinkedIn Ads</h4>
                                        </div>
                                        <div className="p-4">
                                            <div className="grid grid-cols-2 gap-3">
                                                {[
                                                    { label: 'Campaign', value: lead.li_campaign_name },
                                                    { label: 'Form Name', value: lead.li_form_name }
                                                ].filter(item => item.value).map(({ label, value }) => (
                                                    <div key={label} className="bg-gray-50 rounded-lg p-3">
                                                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</span>
                                                        <p className="text-sm font-semibold text-gray-800 mt-1 truncate" title={value}>{value}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Click IDs Card */}
                                {(lead.gclid || lead.fclid) && (
                                    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                                        <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 border-b border-gray-200">
                                            <div className="p-2 bg-amber-500 rounded-lg">
                                                <MousePointerClick size={16} className="text-white" />
                                            </div>
                                            <h4 className="font-semibold text-gray-800">Click Tracking IDs</h4>
                                        </div>
                                        <div className="p-4 space-y-3">
                                            {lead.gclid && (
                                                <div className="bg-gray-50 rounded-lg p-3">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Google Click ID (GCLID)</span>
                                                        <span className="px-1.5 py-0.5 bg-red-100 text-red-600 text-[10px] font-bold rounded">Google</span>
                                                    </div>
                                                    <p className="text-xs font-mono text-gray-600 break-all bg-white p-2 rounded border">{lead.gclid}</p>
                                                </div>
                                            )}
                                            {lead.fclid && (
                                                <div className="bg-gray-50 rounded-lg p-3">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Facebook Click ID (FCLID)</span>
                                                        <span className="px-1.5 py-0.5 bg-blue-100 text-blue-600 text-[10px] font-bold rounded">Meta</span>
                                                    </div>
                                                    <p className="text-xs font-mono text-gray-600 break-all bg-white p-2 rounded border">{lead.fclid}</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* No marketing data message */}
                                {!lead.utm_source && !lead.utm_campaign && !lead.utm_medium && !lead.fb_campaign_name && !lead.li_campaign_name && !lead.gclid && !lead.fclid && (
                                    <div className="text-center py-12">
                                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                                            <BarChart3 size={32} className="text-gray-400" />
                                        </div>
                                        <p className="text-gray-600 font-medium">No Marketing Attribution</p>
                                        <p className="text-gray-400 text-sm mt-1 max-w-xs mx-auto">This lead came through direct entry or an untracked source</p>
                                    </div>
                                )}
                                </div>
                                </>
                            )}
                            </div>
                        </div>
                    )}

                    {/* Timeline Tab */}
                    {activeTab === 'timeline' && lead && (
                        <div className="flex flex-col h-full">
                            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-3 border-b border-blue-100 flex-shrink-0">
                                <h3 className="text-base font-semibold text-gray-800">Timeline</h3>
                                <p className="text-xs text-gray-500 mt-0.5">Notes, calls, status changes & disposition history</p>
                            </div>
                            <div className="flex h-full overflow-hidden">
                                <div className="w-1/2 border-r border-gray-100 overflow-hidden flex flex-col">
                                    <LeadTimeline leadUuid={lead.uuid} />
                                </div>
                                <div className="w-1/2" />
                            </div>
                        </div>
                    )}


                    {/* Reminders Tab */}
                    {activeTab === 'reminders' && (
                        <div>
                            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-blue-100">
                                <h3 className="text-lg font-semibold text-gray-800">Reminders</h3>
                            </div>
                            <div className="p-4">
                            <div className="space-y-6">
                                {/* Add New Reminder Form */}
                                <div className="bg-gray-50 rounded-lg p-4">
                                    <ReminderForm
                                        leadUuid={lead?.uuid}
                                        showDelete={false}
                                        isActivity={false}
                                        onCancel={() => {}}
                                        refreshData={() => {
                                            if (lead) {
                                                // Only refresh reminders since we just created one
                                                setLoadedTabs(prev => {
                                                    const newSet = new Set(prev);
                                                    newSet.delete('marketing');
                                                    newSet.delete('reminders');
                                                    return newSet;
                                                });
                                                fetchRemindersOnly();
                                            }
                                        }}
                                    />
                                </div>

                                {/* Existing Reminders */}
                                <div>
                                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                                        Existing Reminders ({reminders.length})
                                    </h3>

                                    {loadingTabs.has('reminders') ? (
                                        <div className="text-center py-8">
                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brandColor mx-auto"></div>
                                            <p className="text-gray-500 mt-2">Loading reminders...</p>
                                        </div>
                                    ) : reminders.length === 0 ? (
                                        <div className="text-center py-8 bg-gray-50 rounded-lg">
                                            <div className="text-gray-400 mb-2">
                                                <Bell size={48} className="mx-auto" />
                                            </div>
                                            <p className="text-gray-500 text-sm">No reminders set</p>
                                            <p className="text-gray-400 text-xs mt-1">Set a reminder above to get notified about this lead</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            {reminders.map((reminder) => (
                                                <div key={reminder.uuid} className="bg-white border rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
                                                    <div className="flex items-start justify-between">
                                                        <div className="flex-1">
                                                            <h4 className="font-medium text-gray-900">{reminder.event_title}</h4>
                                                            <div className="mt-2 space-y-1">
                                                                <p className="text-sm text-gray-600">
                                                                    <span className="font-medium">Date & Time:</span> {safeFormatDate(reminder.db_start, 'PPp')}
                                                                </p>
                                                                {reminder.alert_before && (
                                                                    <p className="text-sm text-gray-600">
                                                                        <span className="font-medium">Alert:</span> {reminder.alert_before} min before
                                                                    </p>
                                                                )}
                                                                <p className="text-xs text-gray-400">
                                                                    Created: {safeFormatDate(reminder.event_created_at || reminder.created_at, 'PP')}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-2 ml-4">
                                                            <div className="w-8 h-8 bg-yellow-100 text-yellow-600 rounded-full flex items-center justify-center">
                                                                <Bell size={16} />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                            </div>
                        </div>
                    )}

                    {/* Calls Tab */}
                    {activeTab === 'calls' && (
                        <div>
                            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-blue-100">
                                <h3 className="text-lg font-semibold text-gray-800">Call History</h3>
                            </div>
                            <div className="p-4">
                            {loadingTabs.has('calls') ? (
                                <div className="flex items-center justify-center py-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brandColor"></div>
                                    <span className="ml-2 text-gray-600">Loading call history...</span>
                                </div>
                            ) : callHistory.length === 0 ? (
                                <div className="text-center py-12 bg-gray-50 rounded-lg">
                                    <div className="text-gray-400 mb-3">
                                        <Phone size={48} className="mx-auto" />
                                    </div>
                                    <p className="text-gray-600 font-medium text-lg mb-1">No Calls Yet</p>
                                    <p className="text-gray-500 text-sm">Call history will appear here once you make calls to this lead</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {callHistory.map((call) => {
                                        const agentName = call.agent_name || call.raw_payload?.agent_name;
                                        const agentPhone = call.raw_payload?.agent_number || call.agent_number;
                                        const hangupCause = call.hangup_cause || call.raw_payload?.hangup_cause;
                                        const duration = call.total_duration ? (typeof call.total_duration === 'string' ? parseInt(call.total_duration) : call.total_duration) : 0;
                                        const formatPhone = (phone: string) => phone?.startsWith('91') && phone.length === 12 ? phone.slice(2) : phone;

                                        return (
                                            <div key={call.uuid} className="bg-white border rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow">
                                                {/* Header */}
                                                <div className="flex items-center justify-between mb-2">
                                                    <div className="flex items-center gap-2">
                                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                                            call.call_type === 'outbound'
                                                                ? 'bg-blue-100 text-blue-800'
                                                                : 'bg-green-100 text-green-800'
                                                        }`}>
                                                            {call.call_type === 'outbound' ? '↗ Outbound' : '↙ Inbound'}
                                                        </span>
                                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                                            call.is_missed || call.call_status === 'missed'
                                                                ? 'bg-red-100 text-red-800'
                                                                : call.call_status === 'completed'
                                                                ? 'bg-green-100 text-green-800'
                                                                : call.call_status === 'initiated'
                                                                ? 'bg-blue-100 text-blue-800'
                                                                : 'bg-gray-100 text-gray-800'
                                                        }`}>
                                                            {call.is_missed ? 'Missed' : call.call_status}
                                                        </span>
                                                        {hangupCause && (
                                                            <span className="text-xs text-gray-500">({hangupCause})</span>
                                                        )}
                                                    </div>
                                                    <span className="text-xs text-gray-500">
                                                        {safeFormatDate(call.start_time || call.created_at, 'dd MMM, hh:mm a')}
                                                    </span>
                                                </div>

                                                {/* Agent Info */}
                                                {agentName && (
                                                    <div className="flex items-center gap-2 text-sm text-gray-700 mb-2">
                                                        <User size={14} className="text-gray-400" />
                                                        <span className="font-medium">{agentName}</span>
                                                        {agentPhone && (
                                                            <span className="text-gray-500 text-xs">({formatPhone(agentPhone)})</span>
                                                        )}
                                                    </div>
                                                )}

                                                {/* Call Details Grid */}
                                                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                                                    <div>
                                                        <span className="text-gray-500 text-xs">Duration</span>
                                                        <p className="font-medium text-gray-900">
                                                            {duration > 0 ? (duration >= 60 ? `${Math.floor(duration / 60)}m ${duration % 60}s` : `${duration}s`) : '-'}
                                                        </p>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-500 text-xs">Provider</span>
                                                        <p className="font-medium text-gray-900 capitalize">
                                                            {call.provider === 'tata_tele' ? 'Smartflo' : call.provider}
                                                        </p>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-500 text-xs">Called</span>
                                                        <p className="font-medium text-gray-900">{formatPhone(call.destination_number)}</p>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-500 text-xs">Caller ID</span>
                                                        <p className="font-medium text-gray-900">{formatPhone(call.caller_number)}</p>
                                                    </div>
                                                </div>

                                                {/* Recording */}
                                                {call.recording_url && (
                                                    <div className="mt-2 pt-2 border-t border-gray-100">
                                                        <audio controls className="w-full" style={{ height: '32px' }}>
                                                            <source src={call.recording_url} type="audio/mpeg" />
                                                        </audio>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            </div>
                        </div>
                    )}
                    </div>
                </div>
            </div>

            {/* Initiate Call Modal */}
            {isCallModalOpen && lead && (
                <InitiateCallModal
                    isOpen={isCallModalOpen}
                    onClose={handleCloseCallModal}
                    leadUuid={lead.uuid}
                    leadName={lead.name || 'Unknown'}
                    leadPhone={lead.phone || ''}
                />
            )}

            {/* Floating Action Button for Order Creation - Only for E-commerce */}
            {isEcommerce && !isOrderPanelOpen && (
                <button
                    onClick={() => setIsOrderPanelOpen(true)}
                    className="fixed bottom-6 right-6 z-[80] flex items-center gap-2 px-4 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200 group"
                    title="Create Order"
                >
                    <ShoppingCart className="h-5 w-5" />
                    <span className="font-medium">Create Order</span>
                </button>
            )}

            {/* Floating Order Panel */}
            <FloatingOrderPanel
                isOpen={isOrderPanelOpen}
                onClose={() => setIsOrderPanelOpen(false)}
                lead={lead}
                defaultAddress={shopifyCustomer?.default_address ?? null}
            />
        </>
    );
};

export default LeadDetailsSidebar;